import {
  AdminInput,
  AdminSpinner,
  Button,
  Div,
  ErrorToast,
  ListSpinner,
  ModalPortal,
  P,
  PropsModal,
  Radio,
  agentImage,
  getAllUsersWithRoles,
  lightTheme,
  reportError,
} from '@vgrubs/components';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { GetAllUsersResponse } from 'components/src/types/api/user.types';
import { User, UserRole } from 'components/src/types/user.types';
import debounce from 'lodash.debounce';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export const AddLocationAgentModal = ({
  modalIsOpen,
  closeModal,
  existingUserIds,
  onConfirm,
}: PropsModal & {
  modalIsOpen: boolean;
  existingUserIds: number[];
  onConfirm: (userId: number) => Promise<boolean>;
}) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [users, setUsers] = useState<GetAllUsersResponse[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  async function getAllUsers(search: boolean, page: number, hasMore: boolean) {
    if (!hasMore) return;

    try {
      if (search) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const { data } = await getAllUsersWithRoles({
        limit: 20,
        page,
        query,
        roles: [UserRole.salesAgent],
      });

      if (search) {
        setUsers(() => [...data.data]);
        setHasMore(data.data.length > 0);
      } else if (data.data[0]) {
        setHasMore(true);
        setUsers((prevItems) => [...prevItems, ...data.data]);
      } else {
        setHasMore(false);
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (modalIsOpen) {
      setSelectedUser(null);
      setHasMore(true);
      setPage(1);
      getAllUsers(true, 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, modalIsOpen]);

  // Reset the search when the modal closes so a stale query (the input is
  // uncontrolled and clears on reopen) isn't reused on the next open.
  useEffect(() => {
    if (!modalIsOpen) {
      debouncedResults.cancel();
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  useEffect(() => {
    if (inView && users.length > 0) {
      getAllUsers(false, page, hasMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const debouncedResults = useMemo(() => debounce(handleChange, 500), []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const handleConfirm = async () => {
    if (!selectedUser) return ErrorToast('Choose an agent');

    const success = await onConfirm(selectedUser.id);
    if (success) {
      setSelectedUser(null);
      closeModal?.();
    }
  };

  // Hide agents already assigned to the location.
  const availableUsers = users.filter(
    ({ user }) => !existingUserIds.includes(user.id)
  );

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalPortal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Add Agent"
        zIndex={141}
        $numOfFloatingButtons={1}
        footer={
          <Button $mt="40" $w="100%" $selected onClick={handleConfirm}>
            Add
          </Button>
        }
      >
        <AdminInput
          maxWidth="100%"
          maxWidthSM="100%"
          search
          placeholder="Search Agent"
          onChange={debouncedResults}
          mb="20"
        />

        {availableUsers.map(({ user }, i) => (
          <AgentUserComponent
            key={i}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            user={user}
          />
        ))}

        {availableUsers.length === 0 && !loading && (
          <NoDataComponent
            image={agentImage}
            description="No agents were found"
            height="200px"
            heightSM="200px"
            imageWidth="100px"
            imageWidthSM="80px"
          />
        )}

        <ListSpinner loading={loadingMore} observerRef={ref} />
      </ModalPortal>
    </>
  );
};

const AgentUserComponent = ({
  selectedUser,
  setSelectedUser,
  user,
}: {
  selectedUser: User | null;
  setSelectedUser: Dispatch<SetStateAction<User | null>>;
  user: User;
}) => {
  const isSelected = selectedUser?.id === user.id;

  return (
    <Div
      $box
      $p="18px 24px"
      $mb="8"
      $flex
      $alignCenter
      $between
      onClick={() => setSelectedUser(user)}
      $gap="16px"
      $pointer
      $m="8px 0"
    >
      <div>
        <P
          $bold
          $mb="0"
          $pointer
          $hoverUnderline
          $color={isSelected && lightTheme.primary}
          onClick={(e) => {
            e.stopPropagation();
            window.open(`/users/agent/${user.id}`, '_blank');
          }}
        >
          {user?.firstName} {user?.lastName}
        </P>
      </div>

      <Radio checked={isSelected} />
    </Div>
  );
};
